import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaCopy, FaUserPlus, FaCheckCircle, FaHourglassHalf } from 'react-icons/fa';
import { getBusinessInviteInfo } from '../../../../redux/modules/profile/profile.action';

/**
 * İşletme (USER2) profili: "Usta Davet Et" davet kodu + "Bağlı Ustalar" listesi.
 * Usta, bu kodu kayıt formunda VKN yerine girerek işletmeye bağlanır.
 */
const ConnectedChefs = () => {
  const dispatch = useDispatch();
  const inviteInfo = useSelector((store) => store.profile.businessInviteInfo);

  useEffect(() => {
    dispatch(getBusinessInviteInfo());
  }, [dispatch]);

  const inviteCode = inviteInfo?.inviteCode;
  const limit = inviteInfo?.limit ?? 3;
  const usedCount = inviteInfo?.usedCount ?? 0;
  const remaining = inviteInfo?.remaining ?? Math.max(0, limit - usedCount);
  const chefs = inviteInfo?.connectedChefs || [];
  const quotaFull = remaining <= 0;

  const handleCopy = () => {
    if (inviteCode) {
      navigator.clipboard
        .writeText(inviteCode)
        .then(() => toast.success('Davet kodu kopyalandı!'))
        .catch(() => toast.error('Kopyalama işlemi başarısız oldu.'));
    }
  };

  const emptySlots = Math.max(0, limit - chefs.length);

  return (
    <div className="bg-white rounded-3 shadow-sm border border-gray-100 p-4">
      <div className="border-bottom pb-3 mb-3 d-flex align-items-center justify-content-between">
        <h4 className="fw-bold mb-0 text-dark">Bağlı Ustalar</h4>
        <span
          className="fw-bold"
          style={{ fontSize: 14, color: quotaFull ? '#dc3545' : '#0d6efd' }}
        >
          {usedCount} / {limit}
        </span>
      </div>

      {/* Bağlı Ustalar listesi */}
      <div className="mb-4">
        {chefs.map((chef, index) => (
          <div
            key={index}
            className="d-flex align-items-center gap-3 p-2 mb-2 rounded-3 border"
          >
            <div
              className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
              style={{ width: 38, height: 38, background: '#F3E8FF', color: '#6D28D9' }}
            >
              {(chef.name || '?').substring(0, 2).toUpperCase()}
            </div>
            <div className="flex-grow-1">
              <div className="fw-medium" style={{ fontSize: 13 }}>
                {chef.name || '—'}
              </div>
              <div className="text-muted" style={{ fontSize: 11 }}>
                {chef.registerDate ? `${chef.registerDate} tarihinde eklendi` : ''}
              </div>
            </div>
            {chef.certificateApproved ? (
              <span
                className="d-inline-flex align-items-center gap-1"
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: '#059669',
                  background: '#D1FAE5',
                  padding: '2px 10px',
                  borderRadius: 10,
                  whiteSpace: 'nowrap',
                }}
              >
                <FaCheckCircle style={{ fontSize: 10 }} /> Sertifikalı
              </span>
            ) : (
              <span
                className="d-inline-flex align-items-center gap-1"
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: '#92400E',
                  background: '#FEF3C7',
                  padding: '2px 10px',
                  borderRadius: 10,
                  whiteSpace: 'nowrap',
                }}
              >
                <FaHourglassHalf style={{ fontSize: 10 }} /> Onay Bekliyor
              </span>
            )}
          </div>
        ))}

        {Array.from({ length: emptySlots }).map((_, index) => (
          <div
            key={`empty-${index}`}
            className="text-center text-muted fst-italic p-3 mb-2 rounded-3"
            style={{ border: '1.5px dashed #E2E8F0', fontSize: 12 }}
          >
            <FaUserPlus className="me-2" />
            Bir kontenjan daha kullanılabilir
          </div>
        ))}
      </div>

      {/* Usta Davet Et */}
      <div className="border-top pt-3">
        <h6 className="fw-bold mb-2">Usta Davet Et</h6>
        <p className="text-muted mb-3" style={{ fontSize: 13 }}>
          Bu kodu ustanızla paylaşın. Kod {limit} kez kullanılabilir — her usta kaydında
          bir hak düşer. Bağlı ustalar yalnızca admin onayından sonra bu listede görünür.
        </p>

        {inviteCode ? (
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex align-items-center gap-2 w-100" style={{ maxWidth: 380 }}>
              <div
                className="bg-light text-center fw-bold flex-grow-1 py-2 rounded-3"
                style={{
                  fontSize: 24,
                  letterSpacing: '0.15em',
                  color: '#0d6efd',
                  border: '2px solid #3B82F6',
                  fontFamily: 'monospace',
                }}
              >
                {inviteCode}
              </div>
              <button
                onClick={handleCopy}
                disabled={quotaFull}
                className="btn btn-outline-primary d-flex align-items-center gap-2 fw-bold"
                style={{ fontSize: 14, whiteSpace: 'nowrap' }}
              >
                <FaCopy /> Kopyala
              </button>
            </div>
            <div
              className="mt-2 w-100 d-flex justify-content-between"
              style={{ maxWidth: 380, fontSize: 11, color: '#64748B' }}
            >
              <span>
                {usedCount} / {limit} kullanıldı
              </span>
              <span>{remaining} kullanım hakkı kaldı</span>
            </div>
            {quotaFull && (
              <div
                className="alert alert-warning mt-3 w-100"
                style={{ maxWidth: 380, fontSize: 12 }}
                role="alert"
              >
                Usta kontenjanınız dolmuş (3/3). Yeni usta eklemek için Cargill yetkilinizle
                iletişime geçin.
              </div>
            )}
          </div>
        ) : (
          <div className="alert alert-info" role="alert">
            Davet kodunuz oluşturuluyor veya şu anda kullanılamıyor. Lütfen sayfayı yenileyiniz.
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectedChefs;
